import { useEffect, useMemo } from 'react';
import * as React from 'react';

import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// @ts-ignore - ignore missing declaration files
import ReactCountryFlag from 'react-country-flag';
import { useLocation, useNavigate } from 'react-router-dom';

const LANGUAGES: Record<string, Record<string, string>> = {
  en: {
    countryCode: 'US',
    title: 'English',
  },
  de: {
    countryCode: 'DE',
    title: 'Deutsch',
  },
  es: {
    countryCode: 'ES',
    title: 'Espanol',
  },
  fr: {
    countryCode: 'FR',
    title: 'Français',
  },
  it: {
    countryCode: 'IT',
    title: 'Italiano',
  },
  tr: {
    countryCode: 'TR',
    title: 'Türkçe',
  },
  jp: {
    countryCode: 'JP',
    title: '日本',
  },
  ru: {
    countryCode: 'RU',
    title: 'Русский',
  },
  uk: {
    countryCode: 'UA',
    title: 'Українська',
  },
  ko: {
    countryCode: 'KR',
    title: '한국어',
  },
  pt: {
    countryCode: 'PT',
    title: 'Português',
  },
};

export interface LanguageSelectorProps {
  listItem?: boolean;
  languages: string[];
}

export interface LanguageSelectorState {
  anchorEl?: Element;
  selectedLanguage: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  languages,
  listItem,
}: LanguageSelectorProps) => {
  const [anchorEl, setAnchorEl] = React.useState<Element>();
  const [selectedLanguage, setSelectedLanguage] = React.useState('en');
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // You might want to get the initial language from localStorage or another source
    const savedLanguage = i18n.language || 'en';
    setSelectedLanguage(savedLanguage);
  }, []);

  const changeLanguage = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, lang: string) => {
    event.stopPropagation();

    localStorage.setItem('language', lang);
    setSelectedLanguage(lang);
    setAnchorEl(undefined);
    const segments = pathname.split('/');
    if (/^[a-z]{2}$/.test(segments[1])) {
      segments[1] = lang;
    } else {
      segments.splice(1, 0, lang);
    }

    const newPath = segments.join('/') + search;
    navigate(newPath, { replace: true });
  };

  const handleClick = (e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const countryCode = useMemo(() => {
    return LANGUAGES?.[selectedLanguage] ? LANGUAGES?.[selectedLanguage]?.countryCode : 'US';
  }, [selectedLanguage]);

  const open = Boolean(anchorEl);

  return (
    <>
      {listItem ? (
        <ListItem id="btn_nav_lang" button onClick={handleClick}>
          <ListItemIcon>
            <ReactCountryFlag alt={countryCode} countryCode={countryCode} svg />
          </ListItemIcon>
          <ListItemText primary={t('nav.choose_language')} />
        </ListItem>
      ) : (
        <IconButton id="btn_nav_lang" color="primary" onClick={handleClick}>
          <ReactCountryFlag alt={countryCode} countryCode={countryCode} svg />
        </IconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}>
        <MenuList>
          {languages.map((lang) => {
            if (!LANGUAGES[lang]) return null;
            return (
              <MenuItem key={lang} onClick={(event) => changeLanguage(event, lang)}>
                <ReactCountryFlag
                  style={{ marginRight: 10 }}
                  alt={LANGUAGES[lang].countryCode}
                  countryCode={LANGUAGES[lang].countryCode}
                  svg
                />{' '}
                {LANGUAGES[lang].title}
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
};

export default LanguageSelector;
